// CompanyIconAPIManager.js - API Manager for fetching company icons
class CompanyIconAPIManager {
    constructor() {
        this.baseUrl = 'https://images.financialmodelingprep.com/symbol';
        this.cacheExpiration = 24 * 60; // 24 hours in minutes
        this.imageCache = new Map(); // In-memory cache for loaded images
    }

    /**
     * Get the URL for a company icon by ticker
     * @param {string} ticker - Stock ticker symbol
     * @returns {string} URL to the company icon image
     */
    getIconUrl(ticker) {
        if (!ticker) return null;
        return `${this.baseUrl}/${ticker.toUpperCase()}.png`;
    }

    /**
     * Check if an image URL exists (by attempting to load it)
     * @param {string} url - Image URL to check
     * @returns {Promise<boolean>} True if image exists, false otherwise
     */
    async checkImageExists(url) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
    }

    /**
     * Load and cache an image
     * @param {string} ticker - Stock ticker symbol
     * @returns {Promise<string|null>} Image URL if successful, null otherwise
     */
    async loadIcon(ticker) {
        if (!ticker) return null;

        const cacheKey = `company_icon_${ticker.toUpperCase()}`;
        
        // Check in-memory cache first
        if (this.imageCache.has(ticker.toUpperCase())) {
            return this.imageCache.get(ticker.toUpperCase());
        }

        // Check localStorage cache
        const cachedUrl = getCacheItem(cacheKey);
        if (cachedUrl) {
            // Verify the cached URL still works
            const exists = await this.checkImageExists(cachedUrl);
            if (exists) {
                this.imageCache.set(ticker.toUpperCase(), cachedUrl);
                return cachedUrl;
            } else {
                // Remove invalid cache entry
                try {
                    localStorage.removeItem(cacheKey);
                } catch (e) {
                    console.warn(`Failed to remove invalid cache for ${ticker}:`, e);
                }
            }
        }

        // Fetch new image
        const url = this.getIconUrl(ticker);
        if (!url) return null;

        const exists = await this.checkImageExists(url);
        if (exists) {
            // Cache the URL
            try {
                setCacheItem(cacheKey, url, this.cacheExpiration);
                this.imageCache.set(ticker.toUpperCase(), url);
                return url;
            } catch (error) {
                console.warn(`Failed to cache icon URL for ${ticker}:`, error);
                return url;
            }
        }

        return null;
    }

    /**
     * Batch load icons for multiple tickers
     * @param {string[]} tickers - Array of stock ticker symbols
     * @returns {Promise<Map<string, string>>} Map of ticker to icon URL
     */
    async loadIconsBatch(tickers) {
        if (!tickers || tickers.length === 0) return new Map();

        const results = new Map();
        const loadPromises = tickers.map(async (ticker) => {
            const url = await this.loadIcon(ticker);
            if (url) {
                results.set(ticker.toUpperCase(), url);
            }
        });

        await Promise.all(loadPromises);
        return results;
    }

    /**
     * Static factory method to create singleton instance
     * @returns {Promise<CompanyIconAPIManager>} Singleton instance
     */
    static async create() {
        if (!CompanyIconAPIManager.instance) {
            CompanyIconAPIManager.instance = new CompanyIconAPIManager();
        }
        return CompanyIconAPIManager.instance;
    }
}

window.CompanyIconAPIManager = CompanyIconAPIManager;

